import React, { useState } from 'react';
import { Search, Filter, TrendingUp } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { topProductsData } from '../data/mock';

const TopProducts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [displayCount, setDisplayCount] = useState(12);

  // Get unique categories
  const categories = ['all', ...new Set(topProductsData.products.map(p => p.category))];

  // Filter products
  const filteredProducts = topProductsData.products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const displayedProducts = filteredProducts.slice(0, displayCount);

  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="products" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <div className="inline-flex items-center space-x-2 bg-white rounded-full px-4 py-2 shadow-md border border-gray-200 mb-4">
            <TrendingUp className="w-5 h-5 text-gray-700" />
            <span className="text-sm font-semibold text-gray-700">Most Popular</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
            {topProductsData.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {topProductsData.subtitle}
          </p>
        </div>

        {/* Search and Filter */}
        <div className="max-w-4xl mx-auto mb-12 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 border-gray-300 focus:border-gray-700 focus:ring-gray-700"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-64 h-12 border-gray-300">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-sm text-gray-600 text-center">
            Showing {displayedProducts.length} of {filteredProducts.length} products
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {displayedProducts.map((product, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-200"
            >
              <div className="space-y-3">
                {/* Category Badge */}
                <span className="inline-block bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full">
                  {product.category}
                </span>

                {/* Product Name */}
                <h3 className="text-lg font-bold text-gray-900">
                  {product.name}
                </h3>

                {/* Price and Savings */}
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-2xl font-bold text-gray-800">{product.price}</p>
                    <p className="text-xs text-gray-500">per unit</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-600">{product.savings}</p>
                    <p className="text-xs text-gray-500">savings</p>
                  </div>
                </div>

                {/* Order Button */}
                <Button
                  onClick={scrollToContact}
                  className="w-full bg-gray-800 hover:bg-gray-900 text-white py-2 rounded-lg font-semibold transition-colors duration-200"
                >
                  Order Now
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Load More */}
        {displayCount < filteredProducts.length && (
          <div className="text-center">
            <Button
              onClick={() => setDisplayCount(prev => prev + 12)}
              variant="outline"
              className="border-2 border-gray-800 text-gray-800 hover:bg-gray-50 px-8 py-3 rounded-lg font-semibold transition-all duration-200"
            >
              Load More Products
            </Button>
          </div>
        )}

        {/* Note */}
        <div className="mt-12 max-w-3xl mx-auto bg-white rounded-xl p-6 border border-gray-200">
          <p className="text-center text-gray-600">
            <span className="font-semibold text-gray-900">Can't find what you're looking for?</span> 
            {' '}We have 5000+ products available. Contact our expert team for personalized assistance.
          </p>
        </div>
      </div>
    </section>
  );
};

export default TopProducts;
